import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Icon } from '@ontruck/orc';
import { useTranslation } from 'react-i18next';
import { postRequestResetPwd } from 'utils/network/http';
import { sendEventToSegment } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import { ROUTES } from 'utils/constants/routes';
import ButtonLoading from 'components/ButtonLoading';
import { isEmail, addLeadingZeros } from 'utils/functions';
import UsernameInput from 'components/UsernameInput';

const RecoverPassword = ({ className }) => {
  const [user, setUser] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const [userIsEmail, setUserIsEmail] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('RecoverPassword');

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(false);
      await postRequestResetPwd(isEmail(user) ? user : addLeadingZeros(user));
      sendEventToSegment(TRACKING_EVENTS.REQUEST_PASSWORD);
      setSmsSent(true);
      setUserIsEmail(isEmail(user));
    } catch (err) {
      setError(true);
      console.error('There was an error postRequestResetPwd', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetSendStatus = () => {
    setSmsSent(false);
    setUserIsEmail(false);
  };

  return (
    <div className={className} data-testid="recover-password">
      <div className="recover-password">
        <div className="recover-password__background">
          {!smsSent && (
            <div
              className="recover-password__container"
              data-testid="recover-password__no-sent"
            >
              <Link
                className="recover-password__back"
                to={location => ({ ...location, pathname: ROUTES.LOGIN })}
              >
                <Icon glyph="previous" size="1rem" />
                {t('back')}
              </Link>
              <h2
                className="recover-password__title"
                data-testid="recover-password__title"
              >
                {t('title')}
              </h2>
              <p className="recover-password__text">{t('subtitle')}</p>
              <form className="recover-password__form" onSubmit={handleSubmit}>
                <UsernameInput
                  value={user}
                  handleSetValue={setUser}
                  error={error}
                />
                {error && (
                  <div
                    className="recover-password__error"
                    data-testid="recover-password__error"
                  >
                    {t('userError')}
                  </div>
                )}
                <ButtonLoading
                  loading={loading}
                  type="submit"
                  handleClick={handleSubmit}
                  testId="recover-password_submit"
                  className="recover-password__submit"
                  text={t('sentSMSButton')}
                  disabled={user === ''}
                />
              </form>
            </div>
          )}
          {smsSent && (
            <div
              className="recover-password__container"
              data-testid="recover-password__sent"
            >
              <Link
                className="recover-password__back"
                to={location => ({ ...location, pathname: ROUTES.LOGIN })}
              >
                <Icon glyph="previous" size="1rem" />
                {t('back')}
              </Link>
              <h2
                className="recover-password__title"
                data-testid="recover-password__title"
              >
                {t(
                  `${
                    userIsEmail
                      ? 'messageSentTitleEmail'
                      : 'messageSentTitleSMS'
                  }`
                )}
              </h2>
              <p className="recover-password__text">
                {t(
                  `${
                    userIsEmail
                      ? 'messageSentSubtitleEmail'
                      : 'messageSentSubtitleSMS'
                  }`
                )}{' '}
              </p>
              {userIsEmail && (
                <p
                  className="recover-password__text"
                  data-testid="recover-password__userIsEmail"
                >
                  {t('messageSentSubtitle2Email')}{' '}
                </p>
              )}
              <p className="recover-password__text">
                <span>
                  {t(
                    `${
                      userIsEmail
                        ? 'messageSentSubtitle3Email'
                        : 'messageSentSubtitle3SMS'
                    }`
                  )}
                </span>
                &nbsp;
                <button
                  data-testid="recover-password__resend"
                  className="recover-password__resend"
                  onClick={handleResetSendStatus}
                >
                  {t(
                    `${userIsEmail ? 'resendButtonEmail' : 'resendButtonSMS'}`
                  )}
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

RecoverPassword.propTypes = {
  className: PropTypes.string
};

export default RecoverPassword;
